<div class="picker-card">
  <!-- Header: the range flow shows Start and End as two tabs that also read out both
       values; a single date shows what is being picked. -->
  <div class="picker-header">
    @if (rangeMode) {
      <div class="range-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          class="range-tab"
          [class.active]="step === 'start'"
          [attr.aria-selected]="step === 'start'"
          (click)="goToStep('start')"
        >
          <span class="range-tab-label">{{ 'COMMON.PICKER_START' | translate }}</span>
          <span class="range-tab-value">{{ formatDateTime(tabStart) }}</span>
        </button>
        <button
          type="button"
          role="tab"
          class="range-tab"
          [class.active]="step === 'end'"
          [attr.aria-selected]="step === 'end'"
          (click)="goToStep('end')"
        >
          <span class="range-tab-label">{{ 'COMMON.PICKER_END' | translate }}</span>
          <span class="range-tab-value" [class.empty]="!tabEnd">{{ tabEnd ? formatDateTime(tabEnd) : '—' }}</span>
        </button>
      </div>
    } @else {
      <div class="single-head">
        <span class="range-tab-label">{{ (highlightStart ? 'COMMON.RANGE_END' : 'COMMON.RANGE_START') | translate }}</span>
        <span class="range-tab-value">{{ formatDateTime(pickedDate) }}</span>
      </div>
    }
    <button type="button" (click)="onDiscard()" class="nav-btn close-btn" aria-label="Close picker">
      <mat-icon class="material-symbols-outlined">close</mat-icon>
    </button>
  </div>

  <div class="picker-body">
    <!-- Calendar -->
    <div class="calendar-section">
      <div class="calendar-header">
        <button type="button" (click)="prevMonth()" class="nav-btn" aria-label="Previous month">
          <mat-icon class="material-symbols-outlined">chevron_left</mat-icon>
        </button>
        <h3 class="view-month-year">
          {{ getTranslatedText('MONTHS.' + MONTHS[viewMonth]) || MONTHS[viewMonth] }} {{ viewYear }}
        </h3>
        <button type="button" (click)="nextMonth()" class="nav-btn" aria-label="Next month">
          <mat-icon class="material-symbols-outlined">chevron_right</mat-icon>
        </button>
      </div>

      <!-- Card #358: with a booking start and end, the calendar marks the whole range like
           the date range filter does (band + two end circles); otherwise only the selected day. -->
      @let ends = rangeEnds;
      <div class="calendar-grid" [class.ranged]="!!ends">
        @for (d of DAYS; track d) {
          <div class="day-header">{{ getTranslatedText('DAYS.' + d) || d }}</div>
        }
        @for (week of weeks; track $index) {
          @for (day of week; track $index) {
            @if (day === null) {
              <div class="empty-cell"></div>
            } @else {
              <div
                (click)="selectDay(day)"
                (keydown.enter)="selectDay(day)"
                class="day-cell"
                [ngClass]="dayRangeClasses(day, ends)"
                [class.selected]="day === selectedDate && !ends"
                [class.today]="isToday(day)"
                [class.past]="isPast(day) || isBeforeStart(day)"
                [class.before-start]="isBeforeStart(day)"
                [attr.aria-disabled]="isBeforeStart(day) || null"
                role="button"
                [attr.aria-label]="day + ' ' + MONTHS[viewMonth] + ' ' + viewYear"
                tabindex="0"
              >
                <span class="day-num">{{ day }}</span>
              </div>
            }
          }
        }
      </div>
    </div>

    <!-- Time: half-hour buttons by part of the day, plus an exact-time row -->
    <div class="time-section">
      <div class="time-header">
        <h3 class="section-title">{{ 'COMMON.Set Time' | translate }}</h3>
      </div>

      <div class="period-tabs" role="tablist">
        @for (p of PERIODS; track p.key) {
          <button
            type="button"
            role="tab"
            class="period-tab"
            [class.active]="period.key === p.key"
            [attr.aria-selected]="period.key === p.key"
            (click)="selectPeriod(p)"
          >{{ p.label | translate }}</button>
        }
      </div>

      <div class="time-slots">
        @for (slot of timeSlots; track slot.label + slot.h24) {
          @let blocked = isSlotBlocked(slot);
          @let dur = blocked ? '' : durationLabel(slotDate(slot));
          <button
            type="button"
            class="time-slot"
            [class.selected]="isSlotSelected(slot)"
            [disabled]="blocked"
            (click)="selectSlot(slot)"
          >
            {{ slot.label }}
            @if (dur) {
              <small>{{ dur }}</small>
            }
          </button>
        }
      </div>

      @if (!showExactTime) {
        <button type="button" class="exact-link" (click)="showExactTime = true">
          {{ 'COMMON.PICKER_EXACT_TIME' | translate }}
        </button>
      } @else {
        <div class="exact-time">
          <select [(ngModel)]="selectedHour" (ngModelChange)="onExactTimeChange()" aria-label="Hour">
            @for (h of HOURS_12; track h) {
              <option [value]="h">{{ +h }}</option>
            }
          </select>
          <span>:</span>
          <select [(ngModel)]="selectedMinute" (ngModelChange)="onExactTimeChange()" aria-label="Minute">
            @for (m of MINUTES; track m) {
              <option [value]="m">{{ m }}</option>
            }
          </select>
          <select [(ngModel)]="amPm" (ngModelChange)="onExactTimeChange()" aria-label="AM or PM">
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      }
    </div>
  </div>

  <div class="picker-footer">
    @if (rangeError) {
      <span class="range-error" role="alert">{{ rangeError }}</span>
    }
    <div class="footer-summary">
      @if (pickedDuration) {
        <span class="summary-label">{{ 'COMMON.PICKER_DURATION' | translate }}</span>
        <strong>{{ pickedDuration }}</strong>
      }
    </div>
    <button type="button" (click)="onDiscard()" class="discard-btn">
      {{ 'EMAILS.Discard' | translate }}
    </button>
    <button type="button" #confirmBtn (click)="onConfirm($event)" class="confirm-btn">
      {{ (rangeMode && step === 'start' ? 'COMMON.RANGE_NEXT' : 'QUOTE_TEMPLATE.Select Date') | translate }}
    </button>
  </div>
</div>
